/**
 * Defers analytics events across page navigations (e.g., Stripe checkout redirects).
 * Stores pending events in localStorage and uses a URL param to detect successful return.
 */

import type { DeferredAnalyticsEvent } from '../interfaces/deferred-analytics.interface'
import { logger } from './logger.util'

const STORAGE_KEY = 'saaga_deferred_analytics_event'
const EXPIRY_MS = 30 * 60 * 1000

export const CHECKOUT_COMPLETED_PARAM = 'checkout_completed'

interface StoredDeferredEvent {
  event: DeferredAnalyticsEvent
  timestamp: number
}

export const storeDeferredAnalyticsEvent = (event: DeferredAnalyticsEvent): void => {
  const stored: StoredDeferredEvent = { event, timestamp: Date.now() }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
    logger.debug('[Analytics] Deferred event stored', stored)
  } catch (error) {
    logger.warn('[Analytics] Failed to store deferred event', error)
  }
}

export const consumeDeferredAnalyticsEvent = (): DeferredAnalyticsEvent | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    localStorage.removeItem(STORAGE_KEY)

    const stored: StoredDeferredEvent = JSON.parse(raw)

    if (Date.now() - stored.timestamp > EXPIRY_MS) {
      logger.debug('[Analytics] Deferred event expired, discarding')
      return null
    }

    logger.debug('[Analytics] Deferred event consumed', stored.event)
    return stored.event
  } catch (error) {
    localStorage.removeItem(STORAGE_KEY)
    logger.warn('[Analytics] Failed to read deferred event', error)
    return null
  }
}

export const buildCheckoutSuccessUrl = (baseUrl: string): string => {
  const url = new URL(baseUrl)
  url.searchParams.set(CHECKOUT_COMPLETED_PARAM, '1')
  return url.toString()
}

export const buildCheckoutCancelUrl = (baseUrl: string): string => {
  const url = new URL(baseUrl)
  url.searchParams.delete(CHECKOUT_COMPLETED_PARAM)
  return url.toString()
}

export const isCheckoutReturn = (): boolean => {
  if (typeof window === 'undefined') return false
  const params = new URLSearchParams(window.location.search)
  return params.get(CHECKOUT_COMPLETED_PARAM) === '1'
}

export const cleanCheckoutParam = (): void => {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  url.searchParams.delete(CHECKOUT_COMPLETED_PARAM)
  window.history.replaceState({}, '', url.toString())
}
